import { client } from "./client";
import { decodeConsumable } from "./decode";
import type { PaginatedConsumables, SnipeItConsumable } from "./types";

export async function fetchConsumableById(id: number): Promise<SnipeItConsumable | null> {
  try {
    const res = await client.get<SnipeItConsumable>(`/api/v1/consumables/${id}`);
    return decodeConsumable(res.data);
  } catch {
    return null;
  }
}

export async function fetchConsumablesPage(options: {
  limit: number;
  offset: number;
  sort: string;
  order: "asc" | "desc";
  search?: string;
}): Promise<PaginatedConsumables> {
  const params: Record<string, unknown> = {
    limit: options.limit,
    offset: options.offset,
    sort: options.sort,
    order: options.order,
  };
  if (options.search) params.search = options.search;
  const res = await client.get<{ total: number; rows: SnipeItConsumable[] }>("/api/v1/consumables", { params });
  return {
    total: res.data.total,
    rows: (res.data.rows ?? []).map(decodeConsumable),
  };
}

/** Fetches ALL consumables by walking through pages. */
export async function fetchAllConsumables(): Promise<SnipeItConsumable[]> {
  const pageSize = 500;
  const first = await fetchConsumablesPage({ limit: pageSize, offset: 0, sort: "name", order: "asc" });
  const all = [...first.rows];
  const total = first.total;
  for (let offset = pageSize; offset < total; offset += pageSize) {
    const page = await fetchConsumablesPage({ limit: pageSize, offset, sort: "name", order: "asc" });
    all.push(...page.rows);
  }
  return all;
}

export async function checkoutConsumable(id: number, qty: number): Promise<void> {
  await client.patch(`/api/v1/consumables/${id}`, { qty });
}

export async function checkinConsumable(id: number, qty: number): Promise<void> {
  await client.patch(`/api/v1/consumables/${id}`, { qty });
}

/** Searches consumables by name. */
export async function searchConsumables(query: string): Promise<SnipeItConsumable[]> {
  if (!query) return [];
  const res = await client.get<{ rows: SnipeItConsumable[] }>("/api/v1/consumables", {
    params: { search: query, limit: 50 },
  });
  return (res.data.rows ?? []).map(decodeConsumable);
}
